/**
 * Students Service - API для управления учениками (детьми)
 */

const API_BASE = '/api/students';

/**
 * Получить список учеников с расширенной фильтрацией
 */
export async function getStudents({
  page = 1,
  pageSize = 25,
  search = null,
  isActive = null,
  sortBy = null,
  sortOrder = 'asc',
  organizationIds = null,
  groupIds = null,
  parentIds = null,
  includeNoGroups = null,
  organizationNames = null,
  groupNames = null,
  parentNames = null,
  signal = null,
} = {}) {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('page_size', pageSize);
  if (search) params.append('search', search);
  if (isActive !== null) params.append('is_active', isActive);
  if (sortBy) params.append('sort_by', sortBy);
  if (sortOrder) params.append('sort_order', sortOrder);
  if (includeNoGroups !== null) params.append('include_no_groups', includeNoGroups);

  // Массивы по ID
  if (organizationIds && organizationIds.length > 0) {
    organizationIds.forEach((id) => params.append('organization_ids', id));
  }
  if (groupIds && groupIds.length > 0) {
    groupIds.forEach((id) => params.append('group_ids', id));
  }
  if (parentIds && parentIds.length > 0) {
    parentIds.forEach((id) => params.append('parent_ids', id));
  }
  // Массивы по именам (для Excel-фильтров)
  if (organizationNames && organizationNames.length > 0) {
    organizationNames.forEach((n) => params.append('organization_names', n));
  }
  if (groupNames && groupNames.length > 0) {
    groupNames.forEach((n) => params.append('group_names', n));
  }
  if (parentNames && parentNames.length > 0) {
    parentNames.forEach((n) => params.append('parent_names', n));
  }

  const response = await fetch(`${API_BASE}?${params}`, {
    credentials: 'include',
    signal,
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch students');
  }
  return response.json();
}

/**
 * Получить опции для фильтров таблицы учеников
 */
export async function getStudentFilterOptions() {
  const response = await fetch(`${API_BASE}/filter-options`, { credentials: 'include' });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch filter options');
  }
  return response.json();
}

/**
 * Получить ученика по ID
 */
export async function getStudent(studentId) {
  const response = await fetch(`${API_BASE}/${studentId}`, { credentials: 'include' });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch student');
  }
  return response.json();
}

/**
 * Создать ученика
 */
export async function createStudent(data) {
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create student');
  }
  return response.json();
}

/**
 * Обновить ученика
 */
export async function updateStudent(studentId, data) {
  const response = await fetch(`${API_BASE}/${studentId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update student');
  }
  return response.json();
}

/**
 * Деактивировать ученика
 */
export async function deleteStudent(studentId) {
  const response = await fetch(`${API_BASE}/${studentId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete student');
  }
}

/**
 * Привязать родителя к ученику
 */
export async function linkParent(studentId, parentId, relationshipType = 'parent') {
  const response = await fetch(`${API_BASE}/${studentId}/parents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ parent_id: parentId, relationship_type: relationshipType }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to link parent');
  }
  return response.json();
}

/**
 * Отвязать родителя от ученика
 */
export async function unlinkParent(studentId, parentId) {
  const response = await fetch(`${API_BASE}/${studentId}/parents/${parentId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to unlink parent');
  }
}
